import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/axios';
import { useTranslation } from 'react-i18next';
import {
  BuildingStorefrontIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ClockIcon,
  CalendarIcon,
  PhotoIcon,
  UserIcon,
  UserGroupIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/solid';
import { summarizeBusinessHours, perDayBusinessHours } from '../utils/HoursFormat';
import { getCategoryColor } from '../utils/categoryColors';
import { createFormatEventDate } from '../utils/formatEventDate';

const AdminProviderDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const formatEventDate = createFormatEventDate(i18n.language);
  const [provider, setProvider] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [provRes, approvedRes, pendingRes] = await Promise.all([
          api.get(`/map/listing/${id}`),
          api.get('/admin/events?status=approved'),
          api.get('/admin/events?status=pending'),
        ]);
        setProvider(provRes.data);
        const allEvents = [...(approvedRes.data || []), ...(pendingRes.data || [])];
        setEvents(allEvents.filter(e => {
          const pid = typeof e.provider === 'object' ? e.provider?._id : e.provider;
          return pid === id;
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center py-20">
        <p className="text-gray-500 text-lg">{t('admin.no-results')}</p>
        <Link to="/admin/providers" className="text-[#1d5cab] font-bold mt-4 inline-block">&larr; {t('admin.tabs.providers')}</Link>
      </div>
    );
  }

  const bh = provider.businessHours || null;
  const summary = bh ? summarizeBusinessHours(bh, t) : null;
  const perDay = bh ? perDayBusinessHours(bh, t) : null;
  const isSuggestion = !provider.user;

  return (
    <div className="max-w-3xl mx-auto p-6 mt-6 mb-12">
      {/* Back link */}
      <Link to="/admin/providers" className="inline-flex items-center gap-2 text-sm font-bold text-[#1d5cab] hover:text-[#2372bd] mb-6">
        <ArrowLeftIcon className="h-4 w-4" />
        {t('admin.tabs.providers')}
      </Link>

      {/* Header card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Photo */}
        <div className="h-48 bg-gray-50 relative">
          {provider.photoUrl ? (
            <img src={provider.photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <PhotoIcon className="h-16 w-16 text-gray-200" />
            </div>
          )}
          {/* Status badge */}
          <div className="absolute top-3 right-3">
            <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded shadow-sm border ${
              provider.contractStatus === 'Pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
              provider.contractStatus === 'Active' ? 'bg-green-50 text-green-700 border-green-200' :
              'bg-red-50 text-red-700 border-red-200'
            }`}>
              {t(`admin.status.${provider.contractStatus.toLowerCase()}`)}
            </span>
          </div>
          {/* Category badge */}
          <div className="absolute top-3 left-3">
            <span className="text-[10px] uppercase font-bold px-2.5 py-1 rounded shadow-sm"
              style={{ backgroundColor: getCategoryColor(provider.tier).primary, color: getCategoryColor(provider.tier).textOnPrimary }}>
              {t(`map.${provider.tier.toLowerCase()}`)}
            </span>
          </div>
        </div>

        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{provider.businessName}</h1>

          {/* Suggestion vs Owner badge */}
          {isSuggestion ? (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">
                  {t('admin.providers.community-suggestion')}
                </span>
              </div>
              {(provider.contact?.email || provider.contact?.phone) && (
                <div className="mt-2">
                  <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">
                    {t('admin.providers.suggester-contact')}
                  </span>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {provider.contact.email && (
                      <a href={`mailto:${provider.contact.email}`} className="flex items-center gap-1.5 text-xs text-amber-900 font-medium hover:text-amber-700">
                        <EnvelopeIcon className="h-3.5 w-3.5" />
                        {provider.contact.email}
                      </a>
                    )}
                    {provider.contact.phone && (
                      <a href={`tel:${provider.contact.phone}`} className="flex items-center gap-1.5 text-xs text-amber-900 font-medium hover:text-amber-700">
                        <PhoneIcon className="h-3.5 w-3.5" />
                        {provider.contact.phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
              <div className="flex items-center gap-2 mb-1">
                <UserIcon className="h-4 w-4 text-indigo-500" />
                <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">
                  {t('admin.providers.owner-account')}
                </span>
              </div>
              <span className="text-sm font-bold text-indigo-900">{provider.user?.email}</span>
              {provider.user?.name && (
                <span className="text-xs text-indigo-600/70 ml-2">{provider.user.name}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Info card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
            <BuildingStorefrontIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            {t('map.service-form-title')}
          </h2>

          {provider.details?.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 leading-relaxed">{provider.details.description}</p>
            </div>
          )}

          <div className="space-y-3">
            {provider.location?.address && (
              <div className="flex items-start gap-2.5 text-sm text-gray-600">
                <MapPinIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                <span>{provider.location.address}</span>
              </div>
            )}
            {provider.details?.website && (
              <div className="flex items-center gap-2.5 text-sm">
                <GlobeAltIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <a href={/^https?:\/\//i.test(provider.details.website) ? provider.details.website : `https://${provider.details.website}`}
                   target="_blank" rel="noopener noreferrer" className="text-[#1d5cab] hover:text-[#2372bd] font-medium truncate">
                  {provider.details.website}
                </a>
              </div>
            )}
            {!isSuggestion && provider.contact?.email && (
              <div className="flex items-center gap-2.5 text-sm text-gray-600">
                <EnvelopeIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <a href={`mailto:${provider.contact.email}`} className="hover:text-[#1d5cab]">{provider.contact.email}</a>
              </div>
            )}
            {!isSuggestion && provider.contact?.phone && (
              <div className="flex items-center gap-2.5 text-sm text-gray-600">
                <PhoneIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <a href={`tel:${provider.contact.phone}`} className="hover:text-[#1d5cab]">{provider.contact.phone}</a>
              </div>
            )}
          </div>
        </div>

        {/* Hours card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
            <ClockIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            {t('map.business-hours-title')}
          </h2>
          {summary ? (
            <>
              <p className="text-sm font-medium text-emerald-600 mb-3">{summary}</p>
              {perDay && (
                <div className="space-y-1">
                  {perDay.map(({ day, label }) => (
                    <div key={day} className="flex justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                      <span className="font-bold text-gray-400 uppercase">{t(`map.days.${day.toLowerCase()}`)}</span>
                      <span className="text-gray-700">{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">{t('admin.no-description')}</p>
          )}
        </div>
      </div>

      {/* Events section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
            <CalendarIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            {t('admin.tabs.events')}
          </h2>
          <span className="bg-indigo-600 text-white px-2.5 py-0.5 rounded-full text-[11px] font-bold">
            {events.length}
          </span>
        </div>

        {events.length > 0 ? (
          <div className="space-y-3">
            {events.map(e => (
              <div key={e._id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800 truncate">{e.title}</span>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      e.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      e.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {t(`admin.status.${e.status}`)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                    <ClockIcon className="h-3 w-3" />
                    <span>{formatEventDate(e.start, e.end)}</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase px-2 py-1 rounded ml-3"
                  style={{ backgroundColor: getCategoryColor(e.category).light, color: getCategoryColor(e.category).primary }}>
                  {e.category}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic text-center py-6">{t('admin.no-events')}</p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mt-6">
        <Link to={`/admin/edit-listing/${id}`} className="flex-1 bg-[#1d5cab] text-white text-center py-3 rounded-xl text-sm font-bold hover:bg-[#2372bd] transition-colors">
          {t('general.edit')}
        </Link>
      </div>
    </div>
  );
};

export default AdminProviderDetailPage;
